import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowRight,
  FiBookOpen,
  FiHeadphones,
  FiLayers,
  FiMic,
  FiSliders,
  FiUploadCloud,
  FiVolume2,
  FiZap,
} from 'react-icons/fi';
import { GOOGLE_OAUTH_ENABLED } from '../config/runtime';
import './SplashPage.css';

const FORMATS = [
  {
    icon: <FiMic />,
    title: 'Podcasts',
    body: 'Turn any document into a natural, multi-voice conversation. Pick the depth — from a 5-minute brief to a 60-minute deep dive.',
  },
  {
    icon: <FiBookOpen />,
    title: 'Audiobooks',
    body: 'Convert reports, papers, and chapters into narrated audiobooks you can listen to on the commute or at the gym.',
  },
  {
    icon: <FiVolume2 />,
    title: 'Read-aloud',
    body: 'Instant text-to-speech for quick listening. Paste text or use a document and get clean, natural audio in seconds.',
  },
];

const STEPS = [
  {
    icon: <FiUploadCloud />,
    title: 'Upload a document',
    body: 'Bring a PDF, paper, article, or your own notes.',
  },
  {
    icon: <FiSliders />,
    title: 'Choose your format',
    body: 'Podcast, audiobook, or read-aloud — then set depth, length, voices, and tone.',
  },
  {
    icon: <FiHeadphones />,
    title: 'Listen anywhere',
    body: 'Generate and stream your audio, or save it to your library for later.',
  },
];

const FEATURES = [
  { icon: <FiLayers />, title: 'Multiple voices', body: 'Add up to four distinct speakers for lively, dynamic podcasts.' },
  { icon: <FiSliders />, title: 'Tunable depth & tone', body: 'Dial in brief to exhaustive, with conversational, academic, and more.' },
  { icon: <FiZap />, title: 'Fast streaming', body: 'Audio streams as it generates — no waiting for a full render.' },
  { icon: <FiHeadphones />, title: 'Your library', body: 'Every generation is saved so you can replay it any time.' },
];

export default function SplashPage() {
  return (
    <div className="splash">
      <header className="splash__nav">
        <div className="splash__brand">
          <span className="splash__logo" aria-hidden>
            🎙️
          </span>
          <span className="splash__brandName">DocuCast</span>
        </div>
        <Link className="btn btn--ghost btn--sm" to="/login">
          Sign in
        </Link>
      </header>

      <section className="splash__hero">
        <span className="splash__eyebrow">Documents in. Audio out.</span>
        <h1 className="splash__title">
          Turn your documents into <span className="splash__titleAccent">podcasts, audiobooks</span>, and
          read-aloud audio.
        </h1>
        <p className="splash__subtitle">
          DocuCast reads your files so you don&apos;t have to. Upload a document and generate studio-quality
          audio you can listen to anywhere — in minutes.
        </p>
        <div className="splash__ctaRow">
          <Link className="btn btn--primary btn--lg" to="/login" state={{ mode: 'register' }}>
            Get started free <FiArrowRight />
          </Link>
          <Link className="btn btn--ghost btn--lg" to="/login">
            Sign in
          </Link>
        </div>
        <p className="splash__ctaNote">Free to start — no credit card or phone number needed to try.</p>
      </section>

      <section className="splash__section">
        <h2 className="splash__sectionTitle">Three ways to listen</h2>
        <p className="splash__sectionLead">Pick the format that fits how you want to consume your content.</p>
        <div className="splash__grid splash__grid--3">
          {FORMATS.map((f) => (
            <article key={f.title} className="splash__card splash__card--format">
              <div className="splash__cardIcon" aria-hidden>
                {f.icon}
              </div>
              <h3 className="splash__cardTitle">{f.title}</h3>
              <p className="splash__cardBody">{f.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="splash__section">
        <h2 className="splash__sectionTitle">How it works</h2>
        <div className="splash__steps">
          {STEPS.map((s, i) => (
            <div key={s.title} className="splash__step">
              <div className="splash__stepNum" aria-hidden>
                {i + 1}
              </div>
              <div className="splash__stepIcon" aria-hidden>
                {s.icon}
              </div>
              <h3 className="splash__cardTitle">{s.title}</h3>
              <p className="splash__cardBody">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="splash__section">
        <h2 className="splash__sectionTitle">Built for great audio</h2>
        <div className="splash__grid splash__grid--2">
          {FEATURES.map((f) => (
            <article key={f.title} className="splash__card splash__card--feature">
              <div className="splash__featureIcon" aria-hidden>
                {f.icon}
              </div>
              <div>
                <h3 className="splash__cardTitle">{f.title}</h3>
                <p className="splash__cardBody">{f.body}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="splash__cta">
        <h2 className="splash__ctaTitle">Ready to hear your documents?</h2>
        <p className="splash__ctaSub">
          Start free with ad-supported podcasts, or unlock longer audio, extra voices, audiobooks, and more.
        </p>
        <div className="splash__ctaRow">
          <Link className="btn btn--primary btn--lg" to="/login" state={{ mode: 'register' }}>
            Create your account <FiArrowRight />
          </Link>
          {GOOGLE_OAUTH_ENABLED && (
            <Link className="btn btn--ghost btn--lg" to="/login">
              Continue with Google
            </Link>
          )}
        </div>
      </section>

      <footer className="splash__footer">
        <span>
          <span aria-hidden>🎙️</span> DocuCast
        </span>
        <span className="muted">© {new Date().getFullYear()} DocuCast. All rights reserved.</span>
      </footer>
    </div>
  );
}
